<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            :no-close-on-backdrop="standard_modified"
            :no-close-on-esc="standard_modified"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @shown="focusCode"
            @ok="saveStandard"
        >
            <template #modal-title>
                <div v-if="standard.guid">Edit Standard</div>
                <div v-else>Create Standard</div>
            </template>

            <b-form-group
                v-if="editable_standard.guid"
                label="GUID"
                label-for="guid"
            >
                <b-form-input
                    ref="guid"
                    v-model="editable_standard.guid"
                    readonly
                />
            </b-form-group>

            <b-form-group
                label="Code"
                label-for="number"
            >
                <b-form-input
                    ref="code"
                    v-model="editable_standard.number"
                    :class="{'is-invalid': (validating && !editable_standard.number)}"
                />

                <b-form-invalid-feedback v-if="validating && !editable_standard.number">
                    Code is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                label="State"
                label-for="state"
            >
                <b-form-select
                    ref="state"
                    v-model="editable_standard.state"
                    :options="states"
                    :state="validating && !editable_standard.state ? false : null"
                    value-field="abbreviation"
                    text-field="name"
                />

                <b-form-invalid-feedback :state="validating && !editable_standard.state ? false : null">
                    State is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                label="Subject"
                label-for="subject"
            >
                <b-form-select
                    ref="subject"
                    v-model="editable_standard.subject"
                    :options="subjects"
                    :state="validating && !editable_standard.subject ? false : null"
                    value-field="name"
                    text-field="name"
                />

                <b-form-invalid-feedback :state="validating && !editable_standard.subject ? false : null">
                    Subject is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                label="Year"
                label-for="year"
            >
                <b-form-select
                    ref="year"
                    v-model="editable_standard.year"
                    :options="years"
                    :state="validating && !editable_standard.year ? false : null"
                    value-field="year"
                    text-field="year"
                />

                <b-form-invalid-feedback :state="validating && !editable_standard.year ? false : null">
                    Year is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                ref="subject"
                label="Description"
                label-for="description"
            >
                <b-form-textarea
                    v-model="editable_standard.description"
                    rows="3"
                />
                <b-form-text>
                    Optional
                </b-form-text>
            </b-form-group>

            <b-form-group
                ref="low-grade"
                label="Low Grade"
                label-for="low_grade"
            >
                <b-form-select
                    v-model="editable_standard.low_grade"
                    :state="validating && !editable_standard.low_grade ? false : null"
                    @change="checkLowGrade"
                >
                    <b-form-select-option v-for="(grade, index) in grades" :key="index" :value="grade.grade">
                        <span v-if="grade.grade == 'p' || grade.grade == 0">
                            {{ grade.name }}
                        </span>
                        <span v-else>
                            {{ grade.grade }}
                        </span>
                    </b-form-select-option>
                </b-form-select>

                <b-form-invalid-feedback :state="validating && !editable_standard.low_grade ? false : null">
                    Low Grade is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                ref="high-grade"
                label="High Grade"
                label-for="high_grade"
            >
                <b-form-select
                    v-model="editable_standard.high_grade"
                    :state="validating && !editable_standard.high_grade ? false : null"
                >
                    <b-form-select-option
                        v-for="(grade, index) in grades"
                        :key="index"
                        :value="grade.grade"
                        :disabled="grade.grade < editable_standard.low_grade"
                    >
                        <span v-if="grade.grade == 'p' || grade.grade == 0">
                            {{ grade.name }}
                        </span>
                        <span v-else>
                            {{ grade.grade }}
                        </span>
                    </b-form-select-option>
                </b-form-select>

                <b-form-invalid-feedback :state="validating && !editable_standard.high_grade ? false : null">
                    High Grade is required
                </b-form-invalid-feedback>
            </b-form-group>

            <template #modal-footer>
                <b-button variant="light" @click="cancel">
                    Cancel
                </b-button>
                <b-button :variant="standard.guid ? 'primary' : 'success'" @click="saveStandard">
                    <div v-if="standard.guid">
                        <font-awesome-icon :icon="['fad', 'check-square']" fixed-width/>
                        Save Changes
                    </div>
                    <div v-else>
                        <font-awesome-icon :icon="['fad', 'plus-square']" fixed-width/>
                        Create Standard
                    </div>
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import {mapState} from 'vuex'

import states from '@/common/states'
import grades from '@/common/grades'
import standard_subjects from '@/common/standard_subjects'

export default {
    data: () => ({
        grades: grades,
        subjects: standard_subjects,

        editable_standard: {},
        editable_standard_cache: {},
    }),
    props: [
        'value',
        'standard',
        'standards',
        'years'
    ],
    components: {},
    computed: {
        states() {
            const national = {
                name: 'National',
                abbreviation: 'NA',
            }

            return [national, ...states]
        },
        ...mapState({
            validating: state => state.utilities.validating
        }),
        modal_id() {
            let modal_id = 'standard-modal'
            return modal_id
        },
        standard_modified() {
            let standard_modified = false

            if (!_.isEqual(this.editable_standard, this.editable_standard_cache)) {
                standard_modified = true
            }

            return standard_modified
        }
    },
    watch: {
        value: {
            handler(value) {
                if (value == true) {
                    this.$store.commit('setValidating', false)

                    if (this.standard) {
                        this.editable_standard = _.cloneDeep(this.standard)
                        this.editable_standard_cache = _.cloneDeep(this.standard)
                    }
                }
            }
        }
    },
    methods: {
        checkLowGrade() {
            if (
                this.editable_standard.high_grade &&
                this.editable_standard.high_grade < this.editable_standard.low_grade
            ) {
                this.$set(this.editable_standard, 'high_grade', null)
            }
        },
        saveStandard() {
            this.$store.commit('setValidating', true)

            if (!this.valid()) {
                console.log('dang');
                return false
            }

            if (this.standard.guid) {
                this.axios.post('/api/standards/update/' + this.standard.guid, this.editable_standard)
                    .then(() => {
                        this.$emit('updated')
                    })
            } else {
                this.axios.post('/api/standards/create', this.editable_standard)
                    .then(() => {
                        this.$emit('created')
                    })
            }
        },
        valid() {
            let valid = true

            if (!this.editable_standard.state) {
                console.log('3');
                valid = false
            }
            if (!this.editable_standard.subject) {
                console.log('4');
                valid = false
            }
            if (!this.editable_standard.year) {
                console.log('5');
                valid = false
            }
            if (!this.editable_standard.low_grade) {
                console.log('6');
                valid = false
            }
            if (!this.editable_standard.high_grade) {
                console.log('7');
                valid = false
            }
            return valid
        },
        focusCode() {
            if (
                this.$refs['code'] &&
                !this.editable_standard.code
            ) {
                this.$refs['code'].focus()
            }
        },
        cancel(event) {
            event.preventDefault()

            if (this.standard_modified) {
                this.$swal({
                    html: 'Changes have been made to this standard. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.hide()
                    }
                })
            } else {
                this.hide()
            }
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.editable_standard = {}
            this.editable_standard_cache = {}

            this.$emit('hide')
        }
    }
}
</script>
